import { AuthenticationError } from "@App/apis/rest/ayvens/auth/authenticationError";
import type { AuthApi } from "@App/apis/rest/ayvens/auth/interfaces";

/**
 * AuthService handles authentication against a /login endpoint.
 */
export class AuthService implements AuthApi {
  private readonly baseUrl: string;

  /**
   * Constructs an instance of AuthService.
   * @param baseUrl - The base URL for the API (e.g., "http://localhost:3000").
   *                  If empty, "/login" will be treated as a relative path.
   */
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl;
  }

  /**
   * Authenticates a user with the provided email and password.
   * @param email - The user's email address.
   * @param password - The user's password.
   * @returns A Promise that resolves with the user ID (string) upon successful authentication.
   * @throws {AuthenticationError} if authentication fails (401).
   * @throws {Error} for network issues or other server errors.
   */
  public async login(email: string, password: string): Promise<string> {
    const credentials: LoginCredentials = { email, password };

    const loginUrl = this.baseUrl
      ? `${this.baseUrl}/rest/repareo/frontend/v1/ayvens/login`
      : "/rest/repareo/frontend/v1/ayvens/login";

    let response: Response;
    try {
      response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json", // Good practice to specify expected response type
        },
        body: JSON.stringify(credentials),
      });
    } catch (networkError) {
      // This catches errors from fetch itself (e.g., network down, DNS issue)
      // These are typically TypeError
      throw new Error(`Network error: Unable to reach the login service.`);
    }

    if (response.status === 200) {
      try {
        const data: LoginSuccessResponse = await response.json();
        // Validate that userId is a non-empty string as expected for a UUIDv4
        if (data && data.uuid.length > 0) {
          return data.uuid;
        }

        throw new Error("Login successful, but received invalid or missing userId in response.");
      } catch (jsonError) {
        // This catches errors from response.json() if the body is not valid JSON
        throw new Error(`Failed to parse successful login response.`);
      }
    }

    if (response.status === 401) {
      // Authentication failed
      // Optionally, you could try to parse an error message from the 401 response body:
      // const errorBody = await response.text().catch(() => "No additional error information.");
      // throw new AuthenticationError(`Authentication failed: Invalid email or password. Server details: ${errorBody}`);
      throw new AuthenticationError("Authentication failed: Invalid email or password.");
    }

    // Handle other non-ok statuses (e.g., 400 Bad Request, 500 Internal Server Error)
    const errorText = await response
      .text()
      .catch(() => `Status ${response.status} with no error body.`);
    throw new Error(`Login request failed with status ${response.status}: ${errorText}`);
  }
}
